import LegalPageLayout from '../../components/LegalPageLayout';

export default function SecurityPage() {
  return (
    <LegalPageLayout
      title="Security | EvidentTrail"
      description="EvidentTrail security overview. Learn about our read-only design, tenant isolation, authentication, and data protection measures."
    >
      <h1 className="text-3xl sm:text-4xl font-bold text-white mb-6">Security (Early Access)</h1>

      <p className="text-slate-300 leading-relaxed mb-8">
        EvidentTrail is designed to help teams produce evidence packs from GitHub metadata while minimizing operational risk.
        This page summarizes our security posture at a high level.
      </p>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">Read-only by design</h2>
      <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
        <li>We do not modify your code, repositories, or workflows.</li>
        <li>We connect using minimal read-only permissions required to collect evidence.</li>
        <li>
          Learn more about our{' '}
          <a href="/read-only-github-audit" className="text-primary-400 hover:text-primary-300">
            read-only GitHub audit evidence collection
          </a>{' '}
          approach.
        </li>
      </ul>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">GitHub App permissions</h2>
      <p className="text-slate-300 leading-relaxed mb-4">
        The EvidentTrail GitHub App requests read-only access to collect audit evidence. Here is exactly what each permission is used for:
      </p>
      <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
        <li><span className="text-white font-medium">Metadata (read)</span> — Access repository names and basic info to display your connected repos.</li>
        <li><span className="text-white font-medium">Pull requests (read)</span> — Capture PR lifecycle events, reviews, and approvals for audit evidence.</li>
        <li><span className="text-white font-medium">Checks (read)</span> — Record CI/CD check results to verify tests passed before merge.</li>
        <li><span className="text-white font-medium">Contents (read)</span> — Access commit metadata. By default, only file names and change metrics are stored. See "Diff patch storage" below.</li>
        <li><span className="text-white font-medium">Administration (read)</span> — Monitor branch protection rules for configuration drift detection.</li>
        <li><span className="text-white font-medium">Actions (read)</span> — Monitor deployment environment protection rules (Enterprise tier only).</li>
      </ul>
      <p className="text-slate-300 leading-relaxed mt-4 text-sm">
        All permissions are strictly read-only. We cannot modify code, merge PRs, change settings, or access repository secrets.
      </p>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">Diff patch storage (opt-in)</h2>
      <p className="text-slate-300 leading-relaxed mb-4">
        EvidentTrail offers two modes for handling file change information:
      </p>
      <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
        <li>
          <span className="text-white font-medium">Default (secure)</span> — Only file names and change metrics 
          (lines added/deleted) are stored. No actual code content is captured. You can always view the full diff on GitHub.
        </li>
        <li>
          <span className="text-white font-medium">Opt-in patch storage</span> — Tenant owners can enable storing 
          file-level diffs (patches) for richer audit evidence. This stores the actual code changes and should only be 
          enabled if your organization's policies allow storing code snippets externally.
        </li>
      </ul>
      <p className="text-slate-300 leading-relaxed mt-4 text-sm">
        When patch storage is enabled: patches larger than 50KB are automatically truncated; data is subject to your 
        plan's retention period; stored data is read-only and immutable.
      </p>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">Tenant isolation</h2>
      <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
        <li>Data is logically isolated per tenant.</li>
        <li>Access to data is controlled by tenant membership and roles.</li>
      </ul>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">Authentication and access control</h2>
      <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
        <li>JWT-based authentication</li>
        <li>Role-based access control (Owner/Admin/Viewer)</li>
      </ul>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">Webhook and integration security</h2>
      <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
        <li>Webhook signatures are verified.</li>
        <li>Idempotency protections reduce risk from retries/duplicates.</li>
      </ul>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">Audit logging</h2>
      <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
        <li>Administrative actions (for example role changes, exception status changes, control changes) are recorded for traceability.</li>
      </ul>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">Data protection</h2>
      <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
        <li>Secrets are stored via environment configuration.</li>
        <li>Sensitive tokens are encrypted at rest where applicable.</li>
      </ul>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">Operator console</h2>
      <p className="text-slate-300 leading-relaxed">
        EvidentTrail's operator console is restricted to allowlisted operators and cannot be used to access 
        customer data. The console is disabled by default, non-discoverable, and fully audited.
      </p>

      <h2 className="text-xl sm:text-2xl font-semibold text-white mt-10 mb-4">Reporting security issues</h2>
      <p className="text-slate-300 leading-relaxed">
        If you believe you have found a security vulnerability,{' '}
        <a href="/#contact" className="text-primary-400 hover:text-primary-300">
          contact us
        </a>{' '}
        with details. Please do not publicly disclose vulnerabilities before we have a chance to investigate.
      </p>
    </LegalPageLayout>
  );
}
